/**
 * Kalender (Stitch: maestro_kalender): Monats- und Wochenansicht mit Vor/Zurück-Navigation,
 * Wochentagskopf (Mo–So), Events als farbige Chips (Status-Punkt + Titel + "N G. · Ort",
 * Farbe nach Status via KALENDER_FARBEN), Heute hervorgehoben, Klick -> Event-Detail.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { germanHolidays } from "@maestro/feiertage";
import { useApi } from "../hooks/useApi";
import type { Customer, EventRow, Task } from "../ui/types";
import { anlassAnzeige, eventStatusMeta, firmaOderName, formatDate, formatDateTime, money, type PillTone } from "../ui/format";
import { wertAnzeige } from "../lib/a-la-carte";
import { Button, Drawer, ErrorNote, Icon, LoadingRows, StatusPill } from "../ui/components";
import { openPrintableDocument } from "../lib/print";
import { angebotPfad, vorgangPfad } from "../lib/pfade";
import { useMitHerkunft } from "../lib/rueckweg";
import { useNarrowViewport } from "../hooks/useNarrowViewport";

/**
 * Bughunt 27.08.2026 (Antoine, Live-Screenshot): "Der Kalender soll den Firmennamen anzeigen
 * anstatt Event... und dann brauchen wir drei Farben: gruen fuer gebucht, rot fuer neue Anfrage
 * und gelb fuer in Bearbeitung. Im Kalender muessen also alle Termine stehen auch die Anfragen."
 * Praezisiert per Rueckfrage: stornierte/abgelehnte Termine werden GAR NICHT eingetragen (nicht
 * grau, nicht rot) - "nur abgelehnte stornierte gar nicht eintragen bzw. wieder austragen".
 * Und: "unbearbeitete sind auch in rot einzutragen" - eine frische Anfrage ohne Angebotsentwurf
 * ist ROT, nicht neutral.
 *
 * Zweck (Antoines eigene Begruendung): Kapazitaetsueberblick auf einen Blick - "an dem Tag habe
 * ich bereits X Anfragen... die Hunderter-Gruppe kann er nicht annehmen, weil er hat bereits zwei
 * dreissiger Gruppen" - deshalb muss die Gaestezahl auf JEDEM sichtbaren Chip stehen, nicht nur
 * in der Detailansicht.
 *
 * `null` = ausgeblendet (storniert/abgelehnt). Alle drei sichtbaren Faelle nutzen die bestehenden
 * `KALENDER_FARBEN`-Tone-Farben (kein neues Farbschema noetig): "danger" (rot) fuer unbearbeitet,
 * "warn" (gelb) fuer in Bearbeitung, "success" (gruen) fuer gebucht.
 *
 * `abgelaufenAus` (Antoine, 26.09.2026, Praezisierung zu "Abgelaufene Termine immer im Kalender
 * stehen lassen"): ein 'cancelled'-Vorgang, den der Sweep event-verfall-sweep.ts automatisch
 * verfallen liess (statt einer echten Handstornierung), bekommt NICHT grau/neutral, sondern die
 * Farbe seines VORSTATUS — Antoine woertlich: "Der Betreiber soll immer sehen koennen, welche
 * bestaetigt waren und welche nur angefragt." Rekursiver Lookup ueber denselben Switch statt einer
 * zweiten Farbtabelle, damit Legende/Chip nie auseinanderlaufen koennen. Von Hand storniert
 * (`abgelaufenAus` dann `null`, weil kein Sweep-Log-Eintrag existiert) und abgelehnt bleiben wie
 * bisher ausgeblendet.
 *
 * `offer_draft` (Antoine, 26.09.2026, Entscheidung zu diesem Punkt): rot statt gelb — ein Entwurf
 * heisst "Anfrage da, Angebot noch nicht gesendet", das ist derselbe Fall wie eine frische,
 * unbearbeitete Anfrage ("Der Betreiber soll immer sehen koennen, welche nur angefragt waren"),
 * nicht "in Bearbeitung". Gelb bleibt reserviert fuer "Angebot bereits gesendet".
 */
function kalenderTon(status: string, abgelaufenAus?: string | null): PillTone | null {
  switch (status) {
    case "inquiry":
    case "offer_draft":
      return "danger"; // rot: nur angefragt, Angebot noch nicht gesendet
    case "offer_sent":
    case "no_response":
    case "payment_failed": // braucht Nachfassen, ist kein Storno - bleibt "in Bearbeitung"
    case "balance_due": // Restforderung nach Teilerstattung - ebenso
      return "warn";
    case "offer_chosen":
    case "paid":
      return "success";
    case "completed":
      // F5 (10.09.2026, Antoine freigegebenes Mockup "Eingeklappte Durchgeführt-Ansicht"):
      // durchgefuehrte Termine bekommen einen neutralen statt dem sonst abgeleiteten
      // Erfolgston, damit ein vergangener, erledigter Termin nicht dieselbe Aufmerksamkeit
      // zieht wie ein bevorstehender bestaetigter — bleibt trotzdem eingetragen/klickbar,
      // nur optisch zurueckgenommen (zusaetzlich gedimmt, s. `DayItemChip`).
      return "neutral";
    case "cancelled":
      // Automatisch verfallen (nicht von Hand storniert) -> Farbe des Vorstatus, s. Kommentar oben.
      return abgelaufenAus ? kalenderTon(abgelaufenAus) : null;
    case "offer_declined":
    default:
      return null; // ausgeblendet
  }
}

/** Antoine, 14.10.2026: „Im Kalender müssen die Farben bleiben wie bisher" — die ruhigen grauen
 *  Pillen (#1735) gelten überall, nur hier nicht. Darum die bunten Töne von vor #1735 lokal. */
export const KALENDER_FARBEN: Record<PillTone, string> = {
  neutral: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  info: "bg-[#E7EEF7] text-[#31619B] dark:bg-[#31619B]/20 dark:text-[#9dbbe0]",
  success: "bg-[#E4F1E8] text-[#217A50] dark:bg-[#2E9963]/[0.18] dark:text-[#7bc49a]",
  warn: "bg-[#FAEFD9] text-[#96650D] dark:bg-[#C98A1B]/20 dark:text-[#e0b366]",
  danger: "bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/[0.22] dark:text-[#e39b92]",
  primary: "bg-primary/10 text-primary dark:bg-primary/20 dark:text-[#f0b49b]",
};

/** Legenden-Text zum jeweiligen Ton — EINE Quelle, damit Legende und Chip-Farbe nie auseinanderlaufen. */
const KALENDER_LEGENDE: { tone: PillTone; label: string }[] = [
  { tone: "danger", label: "Neue Anfrage" },
  { tone: "warn", label: "In Bearbeitung" },
  { tone: "success", label: "Gebucht" },
];
/** Dieselben Hex-Werte, die KALENDER_FARBEN[tone] als Textfarbe traegt (der Chip-Punkt lebt von
 *  `bg-current`) - hier als eigener Wert, weil ein `bg-*`-Utility keine `currentColor`-Vererbung
 *  aus einer Elternklasse ziehen kann, die den Ton erst per Klasse setzt. */
const LEGENDE_PUNKT_FARBE: Record<PillTone, string> = {
  neutral: "bg-[#6F675A]",
  info: "bg-[#31619B]",
  success: "bg-[#217A50]",
  warn: "bg-[#96650D]",
  danger: "bg-[#B23B32]",
  primary: "bg-primary",
};

/** Eine Kalenderzeile ist ein Event (Termin mit Datum) oder eine Notiz. F2: die dritte, "rohe"
 *  Anfrage-Zeilenart ist entfallen (jede Anfrage hat seit Migration 412 sofort einen Vorgang). */
/** Notiz-/Erinnerungsfunktion (Migration 402, Architektur-Entscheidung C): eine zweite Kalender-
 *  Zeilenart neben Event - eine Notiz (`tasks`) MIT gesetztem Termin. `row.eventId` ist
 *  hier immer gesetzt (der `byDay`-Aufbau unten filtert eventlose Notizen bewusst aus - Klick
 *  navigiert zur Veranstaltung, s. Konzept "Keine Notizen ohne Veranstaltungsbezug in Phase 1"). */
type DayItem = { kind: "event"; row: EventRow; tone: PillTone } | { kind: "note"; row: Task & { eventId: string } };

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function startOfWeek(d: Date): Date {
  const weekday = (d.getDay() + 6) % 7; // Mo=0
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - weekday);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

type Cell = { date: Date; inMonth: boolean };
/** `list` = Handy-Standard (Handy-Mockup 26.09.2026, Entscheidung 7): Wochenstreifen + Tagesliste. */
type View = "month" | "week" | "list";

/** Minimaler Ausschnitt aus DOMRect, den die Popover-Positionierung braucht (leichter zu testen/übergeben). */
interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
}

/**
 * Quick-Win (2026-07): Klick auf einen Termin-Chip navigiert nicht mehr sofort, sondern öffnet ein
 * Vorschau-Popover (siehe EventPreviewPopover unten). `onOpen` bekommt die Bounding-Box des Chips
 * (fürs Anker-Positionieren des Popovers) mit.
 *
 * Bughunt 27.08.2026: generalisiert von "EventChip" auf "KalenderChip" — traegt jetzt Titel/Meta/
 * Ton als fertige Props statt sie aus `EventRow` selbst abzuleiten, damit dieselbe Darstellung
 * auch fuer rohe `inquiries`-Zeilen (kein `EventRow`) funktioniert. `onClick` bekommt die
 * Bounding-Box IMMER mit; wer sie nicht braucht (Anfragen-Klick navigiert direkt, kein Popover),
 * ignoriert den Parameter einfach.
 */
function KalenderChip({
  title,
  meta,
  tone,
  icon,
  dimmed,
  testId,
  onClick,
}: {
  title: string;
  meta: string | null;
  tone: PillTone;
  /** Notiz-/Erinnerungsfunktion (Migration 402): eigenes Icon statt des Farbpunkts, damit ein
   *  Notiz-Termin nie mit einem echten Event-/Anfrage-Chip verwechselbar ist (Konzept
   *  Architektur-Entscheidung C - "eigenes Icon/eigene Farbe"). `undefined` = unveraendertes
   *  Verhalten (Punkt statt Icon) fuer Event-/Anfrage-Chips. */
  icon?: string;
  /** F5 (10.09.2026, freigegebenes Mockup): reduzierte Deckkraft am Chip-Container fuer bereits
   *  durchgefuehrte Termine — bleibt klickbar/oeffnet weiterhin die Vorschau, zieht optisch nur
   *  keinen Blick mehr auf sich. `undefined`/`false` = unveraendertes Verhalten. `"leicht"`
   *  (Antoine, 26.09.2026, Praezisierung zu automatisch abgelaufenen Terminen): schwaechere Stufe,
   *  weil dort — anders als bei "completed" — die Ton-Farbe des Vorstatus klar erkennbar bleiben
   *  soll ("Der Betreiber soll immer sehen koennen, welche bestaetigt waren und welche nur
   *  angefragt"). */
  dimmed?: boolean | "leicht";
  /** E2E-Selektor (`kalender-chip-event-<id>` / `kalender-chip-inquiry-<id>` /
   *  `kalender-chip-note-<id>`) - Text-Matching auf Firmenname allein waere nicht eindeutig genug
   *  (Titel + Meta-Zeile stehen beide als Text IM Button, ausserdem koennten zwei Kunden zufaellig
   *  aehnliche Namen tragen). */
  testId: string;
  onClick: (anchor: AnchorRect) => void;
}) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testId}
      onClick={(ev) => {
        const r = ev.currentTarget.getBoundingClientRect();
        onClick({ top: r.top, bottom: r.bottom, left: r.left });
      }}
      className={`block w-full rounded-md px-1.5 py-1 text-left transition-opacity hover:opacity-80 ${dimmed === true ? "opacity-60" : dimmed === "leicht" ? "opacity-85" : ""}`}
    >
      <span className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 ${KALENDER_FARBEN[tone]}`}>
        {icon ? <Icon name={icon} className="shrink-0 text-[12px]" /> : <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-current" />}
        <span className="truncate font-label-md text-[10.5px] font-semibold leading-tight">{title}</span>
      </span>
      {/* Bughunt 27.08.2026: die Gaestezahl steht jetzt IMMER da (Monat wie Woche) - Antoines
          eigene Begruendung ist Kapazitaetsueberblick ("habe ich an dem Tag schon X Anfragen"),
          das setzt voraus, dass die Zahl auch im engen Monatsraster sichtbar ist, nicht nur nach
          einem zusaetzlichen Klick in die Wochenansicht. */}
      {meta && <span className="mt-0.5 block truncate px-1.5 text-[10px] leading-tight text-secondary-fixed-dim">{meta}</span>}
    </button>
  );
}

/** Titel + Meta-Zeile fuer eine Event-Zeile: Firma vor Person (Bughunt 27.08.2026), Gaestezahl
 *  immer dabei, Ort nur wenn vorhanden. `customer` kommt aus dem client-seitigen
 *  `/api/customers`-Fetch (GET /api/events liefert selbst keine Kundendaten). */
function eventChipText(e: EventRow, customer: Customer | undefined): { title: string; meta: string | null } {
  const title = customer ? firmaOderName(customer) : (anlassAnzeige(e.occasion) ?? "Event");
  // Dezente Kennzeichnung "Abgelaufen" (Antoine, 26.09.2026) — nur Text im Meta-Zeile, keine
  // eigene Chip-Farbe (die traegt weiterhin `kalenderTon`/der Vorstatus).
  const meta = [e.abgelaufenAus ? "Abgelaufen" : null, e.guestCount ? `${e.guestCount} Gäste` : null, e.location].filter(Boolean).join(" · ") || null;
  return { title, meta };
}

/** F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): `inquiryChipText`/die rohe Anfrage-Kalenderzeile
 *  entfielen hier — seit Migration 412 hat jede Anfrage sofort einen Vorgang, `GET /api/events`
 *  deckt den Kalender damit vollstaendig ab (kein zweiter, "roher" Termin ohne Event mehr moeglich). */

/** Notiz-Chip-Text: IMMER mit dem festen Praefix "Notiz: " (Konzept Entscheidung 5, Antoine
 *  woertlich: "Im Kalender muss als Praefix immer stehen: Notiz"), keine Meta-Zeile (eine Notiz
 *  hat keine Gaestezahl/keinen Ort). */
function noteChipText(t: Task): { title: string; meta: string | null } {
  return { title: `Notiz: ${t.title}`, meta: null };
}

/** Waehlt Event-/Anfrage-/Notiz-Darstellung fuer eine Kalenderzeile — EINE Stelle statt der
 *  Verzweigung an jeder Aufrufstelle (Monats- UND Wochenraster). */
function DayItemChip({
  item,
  customerById,
  onOpenEvent,
  onOpenNote,
}: {
  item: DayItem;
  customerById: Map<string, Customer>;
  onOpenEvent: (e: EventRow, anchor: AnchorRect) => void;
  onOpenNote: (t: Task & { eventId: string }) => void;
}) {
  if (item.kind === "event") {
    const { title, meta } = eventChipText(item.row, customerById.get(item.row.customerId));
    // F5: der Chip einer bereits durchgefuehrten ODER automatisch verfallenen Veranstaltung wird
    // gedimmt. Bei "completed" kommt der Ton bereits neutral aus `kalenderTon` oben (volle Stufe).
    // Bei einem automatisch verfallenen Vorgang bleibt die Farbe des Vorstatus erhalten (Antoine,
    // 26.09.2026) — nur eine LEICHTE Stufe, damit sie klar erkennbar bleibt.
    const dimmed: boolean | "leicht" = item.row.status === "completed" ? true : item.row.abgelaufenAus ? "leicht" : false;
    return (
      <KalenderChip
        title={title}
        meta={meta}
        tone={item.tone}
        dimmed={dimmed}
        testId={`kalender-chip-event-${item.row.id}`}
        onClick={(anchor) => onOpenEvent(item.row, anchor)}
      />
    );
  }
  const { title, meta } = noteChipText(item.row);
  return (
    <KalenderChip title={title} meta={meta} tone="info" icon="sticky_note_2" testId={`kalender-chip-note-${item.row.id}`} onClick={() => onOpenNote(item.row)} />
  );
}

/** Angebotsstatus als kompakte Zeile: Version, ob/wann gesendet, gültig-bis bzw. gewählt. */
function offerStatusText(e: EventRow): string {
  if (e.currentOfferVersion <= 0) return "Kein Angebot erstellt";
  const parts = [`v${e.currentOfferVersion}`];
  if (e.offerSentAt) parts.push(`gesendet ${formatDate(e.offerSentAt)}`);
  if (e.status === "offer_chosen" || e.status === "paid" || e.status === "completed") parts.push("gewählt");
  else if (e.offerValidUntil) parts.push(`gültig bis ${formatDate(e.offerValidUntil)}`);
  return parts.join(" · ");
}

/**
 * Inhalt des Vorschau-Popovers (G: Kalender-Klick-Popup) — von Popover (Desktop) UND Drawer
 * (schmale Viewports) gleichermaßen genutzt, damit es nur EINE Stelle mit der eigentlichen
 * Darstellung gibt. `base` ist die bereits geladene Listenzeile (sofort verfügbar), `detail` der
 * On-Demand-Nachlade von GET /api/events/:id (liefert zusätzlich `customerName`, s. Backend).
 */
function EventPreviewBody({
  base,
  detail,
  detailLoading,
  customer,
  onOpen,
  onOffer,
  onPrint,
  printBusy,
}: {
  base: EventRow;
  detail: EventRow | null;
  detailLoading: boolean;
  /** Bughunt 27.08.2026: aus dem client-seitig geladenen `/api/customers` — synchron da, kein
   *  Warten auf den Detail-Nachlade wie bisher beim Kundennamen. */
  customer: Customer | undefined;
  onOpen: () => void;
  onOffer: () => void;
  onPrint: () => void;
  printBusy: boolean;
}) {
  // Antoine, 26.09.2026: `detail` (GET /api/events/:id) liefert `abgelaufen*` NICHT mit (nur die
  // Liste tut das, s. routes/events.ts) - ein Merge statt eines reinen Ersetzens haelt diese Felder
  // aus `base` auch nach dem Nachladen sichtbar, ohne die Detail-Route dafuer zu erweitern.
  const e = { ...base, ...detail };
  const meta = eventStatusMeta(e.status);
  // Bughunt 27.08.2026: Firma vor Person statt Anlass als Ueberschrift (Antoines Wunsch gilt
  // auch fuer die Detail-Vorschau, nicht nur die Kalender-Chips). Der Anlass steht direkt darunter
  // weiter - nichts geht verloren, nur die Prioritaet dreht sich um.
  const title = customer ? firmaOderName(customer) : (anlassAnzeige(e.occasion) ?? "Termin");
  const timeRange = e.timeFrom ? `${e.timeFrom.slice(0, 5)}${e.timeTo ? `–${e.timeTo.slice(0, 5)}` : ""} Uhr` : null;
  return (
    <div className="space-y-3.5">
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0">
          <p className="truncate font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{title}</p>
          {e.occasion && <p className="truncate text-[11.5px] text-secondary-fixed-dim">{anlassAnzeige(e.occasion)}</p>}
        </div>
        <StatusPill meta={meta} />
      </div>
      <div className="space-y-1.5 font-body-sm text-[12.5px] text-on-surface-variant">
        <p className="flex items-center gap-1.5">
          <Icon name="calendar_today" className="shrink-0 text-[15px]" />
          {formatDate(e.eventDate)}
          {timeRange ? ` · ${timeRange}` : ""}
        </p>
        {/* Antoine, 26.09.2026 ("Nachtraeglich alle bisherigen wieder einfuegen"): reine Anzeige,
            welcher Vorgang der Sweep automatisch verfallen liess statt einer Handstornierung. */}
        {e.abgelaufenAus && (
          <p className="flex items-center gap-1.5">
            <Icon name="event_busy" className="shrink-0 text-[15px]" />
            Automatisch abgelaufen{e.abgelaufenAm ? ` am ${formatDateTime(e.abgelaufenAm)}` : ""}
          </p>
        )}
        {e.guestCount != null && (
          <p className="flex items-center gap-1.5">
            <Icon name="group" className="shrink-0 text-[15px]" /> {e.guestCount} Gäste
          </p>
        )}
        {e.location && (
          <p className="flex items-center gap-1.5">
            <Icon name="location_on" className="shrink-0 text-[15px]" /> {e.location}
          </p>
        )}
        <p className="flex items-center gap-1.5">
          <Icon name="person" className="shrink-0 text-[15px]" />
          {/* Bughunt 27.08.2026: `customer` (aus /api/customers) ist synchron da; der Detail-
              Nachlade (GET /api/events/:id) bleibt nur noch Rueckfall, falls die Kundenliste noch
              laedt oder der Datensatz dort fehlt. */}
          {customer ? customer.name : detail ? (detail.customerName ?? "—") : detailLoading ? "Lädt …" : "—"}
        </p>
        <p className="flex items-center gap-1.5">
          <Icon name="request_quote" className="shrink-0 text-[15px]" /> {offerStatusText(e)}
        </p>
        {e.amountTotalCents != null && (
          <p className="flex items-center gap-1.5 font-semibold text-on-background dark:text-inverse-on-surface">
            <Icon name="payments" className="shrink-0 text-[15px]" /> {wertAnzeige(e, money)}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
        <Button type="button" onClick={onOpen} className="!py-1.5 !text-[12px]">
          <Icon name="open_in_new" className="text-[15px]" /> Öffnen
        </Button>
        {e.currentOfferVersion > 0 && (
          <Button type="button" variant="secondary" onClick={onOffer} className="!py-1.5 !text-[12px]">
            <Icon name="request_quote" className="text-[15px]" /> Zum Angebot
          </Button>
        )}
        {e.eventDate && (
          <Button type="button" variant="secondary" onClick={onPrint} disabled={printBusy} className="!py-1.5 !text-[12px]">
            <Icon name="print" className="text-[15px]" /> {printBusy ? "Drucke …" : "Küchenzettel"}
          </Button>
        )}
      </div>
    </div>
  );
}

const POPOVER_WIDTH = 336;
const POPOVER_MARGIN = 10;

/** Fixe Position rechts/unterhalb (oder oberhalb, falls unten kein Platz) des Anker-Chips,
 *  horizontal an den Viewport geklemmt. Bewusst eine simple Ein-Pass-Schätzung (kein Portal,
 *  keine zweite Mess-Runde) — reicht für die kompakte Popover-Größe. */
function popoverStyle(anchor: AnchorRect): CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = anchor.left;
  if (left + POPOVER_WIDTH + POPOVER_MARGIN > vw) left = vw - POPOVER_WIDTH - POPOVER_MARGIN;
  if (left < POPOVER_MARGIN) left = POPOVER_MARGIN;
  const spaceBelow = vh - anchor.bottom;
  const spaceAbove = anchor.top;
  if (spaceBelow >= 320 || spaceBelow >= spaceAbove) {
    return { top: anchor.bottom + POPOVER_MARGIN, left };
  }
  return { bottom: vh - anchor.top + POPOVER_MARGIN, left };
}

/** Schließt bei Klick außerhalb oder Escape — gleiches Muster wie AppShell.tsx (dortiges
 *  `useDismiss`, dort privat und nicht exportiert, hier lokal gespiegelt). */
function useDismiss<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const onDown = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) onClose();
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return ref;
}

/**
 * Anker-Popover (Desktop/breite Viewports). `key={event.id}` am Aufrufort sorgt dafür, dass beim
 * Wechsel zu einem anderen Chip neu gemountet (=> Eintritts-Übergang erneut) statt nur umpositioniert
 * wird. `prefers-reduced-motion` respektiert: der Ein-/Ausblend-Übergang läuft nur unter
 * `motion-safe:` (Projekt-Konvention, siehe CopilotPanel.tsx) — bei reduzierter Bewegung erscheint
 * das Popover direkt ohne Animation.
 */
function EventPreviewPopover({ anchor, onClose, children }: { anchor: AnchorRect; onClose: () => void; children: ReactNode }) {
  const [entered, setEntered] = useState(false);
  const ref = useDismiss<HTMLDivElement>(onClose);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Termin-Vorschau"
      style={{ ...popoverStyle(anchor), width: POPOVER_WIDTH }}
      className={`fixed z-[65] max-h-[70vh] overflow-y-auto rounded-2xl border border-outline-variant/50 bg-surface-container-lowest p-4 shadow-2xl motion-safe:transition-all motion-safe:duration-150 dark:border-outline/40 dark:bg-night-card ${
        entered ? "opacity-100" : "opacity-0"
      }`}
    >
      {children}
    </div>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const mitHier = useMitHerkunft(); // Punkt 115: Sprung in den Vorgang merkt „Kalender“ als Herkunft
  const { data, loading, error } = useApi<EventRow[]>("/api/events");
  /**
   * Bughunt 27.08.2026: eigene Datenquelle fuer "Firma statt Event". `GET /api/events` liefert
   * keine Kundendaten (nur `customerId`) - derselbe client-seitige Map-Aufbau wie in payments.tsx
   * (`customerName()`), kein neuer Backend-Endpunkt noetig.
   *
   * F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): der zusaetzliche `GET /api/inquiries`-Abruf
   * fuer "rohe, noch NICHT zu einem Event gewordene Anfragen" ist entfallen — seit Migration 412
   * bekommt jede Anfrage sofort einen Vorgang, `GET /api/events` deckt den Kalender allein ab.
   */
  const customersQ = useApi<Customer[]>("/api/customers");
  const customerById = useMemo(() => new Map((customersQ.data ?? []).map((c) => [c.id, c])), [customersQ.data]);
  // Notiz-/Erinnerungsfunktion (Migration 402, Konzept Architektur-Entscheidung C): mandantenweit
  // (KEIN `?eventId=`-Filter, anders als `AuftragNotizenKarte.tsx`) - der Kalender zeigt Notizen
  // aller Veranstaltungen. Client-seitig unten auf `dueDate != null` gefiltert; da ein Termin
  // zwingend eine Erinnerung braucht (Entscheidung 4), ist das bereits deckungsgleich mit "hat
  // Erinnerung" - kein Join auf `task_reminders` noetig, um zu entscheiden, ob eine Zeile erscheint.
  const notesQ = useApi<Task[]>("/api/tasks");
  // Datenanreicherung Welle A: fehlende tenant_settings-Zeile -> {data: null} -> "alle Flags
  // false" (nie Fehler, nie Default-an) -- Feiertage bleiben dann einfach ausgeblendet.
  const settingsQ = useApi<{ feiertageEnabled?: boolean; bundesland?: string | null } | null>("/api/settings/payment-terms");
  const feiertageEnabled = settingsQ.data?.feiertageEnabled ?? false;
  const bundesland = settingsQ.data?.bundesland ?? null;
  const narrow = useNarrowViewport();
  const [viewWahl, setView] = useState<View>(() => (narrow ? "list" : "month"));
  // Die Tagesliste gibt es nur auf dem Handy; wird das Fenster breit, gilt wieder der Monat.
  const view: View = !narrow && viewWahl === "list" ? "month" : viewWahl;
  /** Handy: Tag, zu dem die Tagesliste nach dem Antippen im Wochenstreifen/Monat springt. */
  const [fokusTag, setFokusTag] = useState<string | null>(null);
  /**
   * WARTESCHLANGE Punkt 56: die Tages-Karte im Angebots-Builder verlinkt hierher mit `?tag=`
   * (`YYYY-MM-DD`). Ohne das landete der Sprung immer im aktuellen Monat — bei einer Anfrage fuer
   * den uebernaechsten Sommer waere das keine Antwort auf „zeig mir diesen Tag".
   * Der Parameter wirkt nur beim ERSTEN Aufbau; blaettert der Betreiber danach weiter, bleibt es
   * dabei (`useState`-Initialisierer, kein Effekt, der ihn wieder zurueckzieht).
   */
  const [suchParameter] = useSearchParams();
  const [cursor, setCursor] = useState(() => {
    const roh = suchParameter.get("tag");
    if (roh && /^\d{4}-\d{2}-\d{2}$/.test(roh)) {
      const [jahr, monat] = roh.split("-").map(Number);
      if (jahr && monat) return new Date(jahr, monat - 1, 1);
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [weekCursor, setWeekCursor] = useState(() => startOfWeek(new Date()));
  // Monatsansicht: Tage (ymd), deren „+N weitere" aufgeklappt ist (Antoine, 14.10.2026).
  const [offeneTage, setOffeneTage] = useState<Set<string>>(() => new Set());
  const [printDate, setPrintDate] = useState<string | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);

  // Vorschau-Popover (Quick-Win 2026-07): `preview` haelt die Listenzeile + Anker-Position des
  // angeklickten Chips. `detailQ` laedt on-demand GET /api/events/:id nach (liefert u.a.
  // customerName, s. Backend-Erweiterung) -- path=null solange nichts offen ist, damit useApi
  // nicht unnoetig alle Events einzeln pollt.
  const [preview, setPreview] = useState<{ event: EventRow; anchor: AnchorRect } | null>(null);
  const detailQ = useApi<EventRow>(preview ? `/api/events/${preview.event.id}` : null);

  function openPreview(e: EventRow, anchor: AnchorRect) {
    setPreview({ event: e, anchor });
  }
  function closePreview() {
    setPreview(null);
  }
  /** Notiz-/Erinnerungsfunktion (Konzept Architektur-Entscheidung C): "kein neues Popover noetig
   *  fuer Notizen - direkter Klick-Navigate reicht". */
  function openNote(t: Task & { eventId: string }) {
    navigate(mitHier(vorgangPfad(t.eventId), "Kalender"));
  }

  // Vor-/Folgemonats-Fueller koennen ins Vor-/Folgejahr reichen -> alle drei Jahre berechnen.
  // Deckt Monats- UND Wochenansicht ab (weekCursor kann nahe Jahreswechsel abweichen).
  const holidaysByDate = useMemo(() => {
    const map = new Map<string, string>();
    if (!feiertageEnabled || !bundesland) return map;
    const years = new Set([cursor.getFullYear(), weekCursor.getFullYear()]);
    for (const year of years) {
      for (const y of [year - 1, year, year + 1]) {
        for (const h of germanHolidays(y, bundesland)) map.set(h.date, h.name);
      }
    }
    return map;
  }, [feiertageEnabled, bundesland, cursor, weekCursor]);

  async function printDay(dateKey: string) {
    setPrintDate(dateKey);
    setPrintError(null);
    try {
      await openPrintableDocument(`/api/events/kitchen-sheet?date=${dateKey}`);
    } catch (err) {
      setPrintError(err instanceof Error ? err.message : String(err));
    } finally {
      setPrintDate(null);
    }
  }

  /**
   * Bughunt 27.08.2026: storniert/abgelehnt komplett ausgeblendet (Antoines Rueckfrage-Antwort:
   * "nur abgelehnte stornierte gar nicht eintragen"). AUSNAHME (Antoine, 26.09.2026, „Abgelaufene
   * Termine immer im Kalender stehen lassen"): ein automatisch (nicht von Hand) verfallener
   * Vorgang bleibt sichtbar — `kalenderTon` erkennt das an `e.abgelaufenAus` und faerbt nach dem
   * Vorstatus statt auszublenden.
   *
   * F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): die zweite Schleife ueber "rohe" Anfragen
   * ohne `generatedEventId` ist entfallen — seit Migration 412 hat jede Anfrage sofort einen
   * Vorgang, `GET /api/events` deckt damit bereits jeden Fall ab (kein Dedup mehr noetig).
   */
  const byDay = useMemo(() => {
    const map = new Map<string, DayItem[]>();
    for (const e of data ?? []) {
      if (!e.eventDate) continue;
      const tone = kalenderTon(e.status, e.abgelaufenAus);
      if (!tone) continue;
      const key = e.eventDate.slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), { kind: "event", row: e, tone }]);
    }
    // Notiz-/Erinnerungsfunktion: nur Notizen MIT Termin UND Veranstaltungsbezug (Konzept
    // Nicht-Ziel "Keine Notizen ohne Veranstaltungsbezug in Phase 1") - eine `eventId`-lose Zeile
    // aus der bestehenden "Aufgaben"-Seite haette sonst kein gueltiges Klick-Ziel.
    for (const t of notesQ.data ?? []) {
      if (!t.dueDate || !t.eventId) continue;
      const key = t.dueDate.slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), { kind: "note", row: t as Task & { eventId: string } }]);
    }
    return map;
  }, [data, notesQ.data]);

  // Monats-Zellen: Vormonats-Fueller (Mo-basiert) + Tage des Monats + Folgemonats-Fueller
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthCells: Cell[] = [];
  const firstWeekday = (cursor.getDay() + 6) % 7; // Mo=0
  for (let i = firstWeekday; i > 0; i--) {
    monthCells.push({ date: new Date(year, month, 1 - i), inMonth: false });
  }
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    monthCells.push({ date: new Date(year, month, d), inMonth: true });
  }
  while (monthCells.length % 7 !== 0) {
    const last = monthCells[monthCells.length - 1]!.date;
    monthCells.push({ date: addDays(last, 1), inMonth: false });
  }

  // Wochen-Zellen: 7 Tage ab weekCursor (Mo)
  const weekCells: Cell[] = Array.from({ length: 7 }, (_, i) => ({ date: addDays(weekCursor, i), inMonth: true }));

  const todayKey = ymd(new Date());
  const monthLabel = cursor.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const weekEnd = addDays(weekCursor, 6);
  const weekLabel =
    weekCursor.getMonth() === weekEnd.getMonth()
      ? `${weekCursor.getDate()}.–${weekEnd.getDate()}. ${weekEnd.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}`
      : `${weekCursor.getDate()}. ${weekCursor.toLocaleDateString("de-DE", { month: "long" })} – ${weekEnd.getDate()}. ${weekEnd.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}`;
  // Bughunt 27.08.2026: zaehlt jetzt ALLE sichtbaren Kalender-Zeilen (Events + rohe Anfragen),
  // nicht nur Events - der Zaehler soll denselben Kapazitaetsueberblick spiegeln wie das Raster.
  const monthPrefix = ymd(new Date(year, month, 1)).slice(0, 7);
  const itemsThisMonth = [...byDay.entries()].filter(([key]) => key.startsWith(monthPrefix)).flatMap(([, items]) => items);

  function goPrev() {
    if (view === "month") setCursor(new Date(year, month - 1, 1));
    else setWeekCursor(addDays(weekCursor, -7));
  }
  function goNext() {
    if (view === "month") setCursor(new Date(year, month + 1, 1));
    else setWeekCursor(addDays(weekCursor, 7));
  }
  /** Handy: Tag antippen (Wochenstreifen oder Punkte-Monat) → Tagesliste dieser Woche, zum Tag scrollen. */
  function zeigeTag(date: Date) {
    setWeekCursor(startOfWeek(date));
    setView("list");
    setFokusTag(ymd(date));
  }
  function goToday() {
    const now = new Date();
    setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
    setWeekCursor(startOfWeek(now));
  }

  return (
    <>
      {/* G3: kein separates Seiten-H1 mehr — der Topbar-Seitentitel ("Kalender") reicht,
          hier nur noch das Monats-/Wochenlabel (22px). */}
      <div className="mb-3.5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="Zurück"
            onClick={goPrev}
            className="grid h-10 w-10 place-items-center rounded-lg border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant transition-colors hover:text-primary dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
          >
            <Icon name="chevron_left" className="text-[18px]" />
          </button>
          <button
            type="button"
            aria-label="Weiter"
            onClick={goNext}
            className="grid h-10 w-10 place-items-center rounded-lg border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant transition-colors hover:text-primary dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
          >
            <Icon name="chevron_right" className="text-[18px]" />
          </button>
        </div>
        <h1 className="text-[22px] font-bold capitalize tracking-[-0.02em] text-on-background dark:text-inverse-on-surface">
          {view === "month" ? monthLabel : weekLabel}
        </h1>
        <button
          type="button"
          onClick={goToday}
          className="rounded-lg border border-outline-variant/60 bg-surface-container-lowest px-3 py-1.5 font-label-md text-[11.5px] font-semibold text-on-surface-variant shadow-sm hover:text-primary dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
        >
          Heute
        </button>

        <div className="ml-auto flex flex-wrap items-center gap-3.5">
          {/* Bughunt 27.08.2026: 3 Farben statt 5 Legenden-Eintraegen (Antoines Vorgabe) - Punkt-
              Hex-Werte sind dieselben, die KALENDER_FARBEN fuer den jeweiligen Ton als Textfarbe nutzt
              (der Chip-Punkt lebt von `bg-current`), damit Legende und Chip niemals auseinanderlaufen.
              Storniert/abgelehnt ist bewusst NICHT in der Legende - diese Zeilen werden gar nicht
              erst eingetragen (Antoines Rueckfrage-Antwort). Feiertag weiterhin mit eigenem Symbol.
              Abgelaufen (26.09.2026) ebenfalls mit eigenem Symbol statt Punkt-Farbe - die Farbe
              selbst ist ja die des Vorstatus, kein eigener Ton. */}
          <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
            {KALENDER_LEGENDE.map(({ tone, label }) => (
              <span key={tone} className="flex items-center gap-1.5">
                <span className={`h-[7px] w-[7px] rounded-full ${LEGENDE_PUNKT_FARBE[tone]}`} /> {label}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" /> Feiertag
            </span>
            <span className="flex items-center gap-1.5">
              <Icon name="event_busy" className="text-[13px] leading-none text-secondary-fixed-dim" /> Abgelaufen
            </span>
          </div>
          {/* G2: Monat/Woche-Toggle; auf dem Handy zusätzlich „Liste“ (Standard dort). */}
          <div className="flex items-center gap-0.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest p-[3px] dark:border-outline/40 dark:bg-inverse-surface">
            {(narrow ? (["list", "week", "month"] as const) : (["month", "week"] as const)).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`rounded-lg px-3.5 py-1.5 font-label-md text-[12px] font-semibold ${
                  view === v ? "bg-primary text-on-primary" : "text-secondary-fixed-dim"
                }`}
              >
                {v === "month" ? "Monat" : v === "week" ? "Woche" : "Liste"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {printError && <ErrorNote message={printError} />}
      {error && <ErrorNote message={error} />}
      {loading ? (
        <LoadingRows rows={6} />
      ) : view === "list" ? (
        <KalenderTagesliste
          tage={weekCells.map((c) => c.date)}
          byDay={byDay}
          todayKey={todayKey}
          holidaysByDate={holidaysByDate}
          fokusTag={fokusTag}
          onTag={zeigeTag}
          customerById={customerById}
          printDate={printDate}
          onPrint={(key) => void printDay(key)}
          onNeu={(key) => navigate(`/anfragen?neu=1&datum=${key}`)}
          onOpenEvent={(e) => openPreview(e, { top: 0, bottom: 0, left: 0 })}
          onOpenNote={openNote}
        />
      ) : narrow && view === "month" ? (
        <KalenderMonatPunkte cells={monthCells} byDay={byDay} todayKey={todayKey} holidaysByDate={holidaysByDate} onTag={zeigeTag} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              {/* Wochentagskopf */}
              <div className="grid grid-cols-7 border-b border-outline-variant/50 bg-surface-container-low/50 dark:border-outline/30 dark:bg-surface-variant/10">
                {WEEKDAYS.map((w) => (
                  <div
                    key={w}
                    className="px-2 py-3 text-center font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim"
                  >
                    {w}
                  </div>
                ))}
              </div>

              {view === "month" ? (
                /* G5: Zellhöhe 120 -> 96px */
                <div className="grid grid-cols-7 gap-px bg-outline-variant/20 dark:bg-outline/20">
                  {monthCells.map(({ date, inMonth }, idx) => {
                    const key = ymd(date);
                    const isToday = key === todayKey;
                    const dayItems = byDay.get(key) ?? [];
                    const hasEvent = dayItems.some((it) => it.kind === "event");
                    const holidayName = holidaysByDate.get(key);
                    // L7: leerer Tag -> Klick legt „Neue Anfrage" mit vorbelegtem Datum an (Route wie Sidebar).
                    const isEmpty = dayItems.length === 0;
                    const openNewInquiry = () => navigate(`/anfragen?neu=1&datum=${key}`);
                    return (
                      <div
                        key={`${key}-${idx}`}
                        onClick={isEmpty ? openNewInquiry : undefined}
                        onKeyDown={
                          isEmpty
                            ? (ev) => {
                                if (ev.key === "Enter" || ev.key === " ") {
                                  ev.preventDefault();
                                  openNewInquiry();
                                }
                              }
                            : undefined
                        }
                        role={isEmpty ? "button" : undefined}
                        tabIndex={isEmpty ? 0 : undefined}
                        title={isEmpty ? `Neue Anfrage für den ${date.getDate()}. anlegen` : undefined}
                        className={`min-h-[104px] p-2 transition-colors ${isEmpty ? "cursor-pointer" : ""} ${
                          isToday
                            ? "bg-primary-fixed/30 dark:bg-primary-fixed/10"
                            : "bg-surface-container-lowest hover:bg-surface-container-low/40 dark:bg-inverse-surface dark:hover:bg-surface-variant/20"
                        }`}
                      >
                        <div className="mb-1 flex items-center justify-between gap-1">
                          {hasEvent ? (
                            <button
                              type="button"
                              title={`Küchenzettel für ${key} drucken (Sammel-Druck)`}
                              disabled={printDate === key}
                              onClick={() => void printDay(key)}
                              className="rounded p-0.5 text-secondary-fixed-dim transition-colors hover:bg-surface-container-high hover:text-primary disabled:opacity-50 dark:hover:bg-surface-variant/40"
                            >
                              <Icon name="print" className="text-[14px]" />
                            </button>
                          ) : (
                            <span />
                          )}
                          {isToday ? (
                            // L8: „Heute" als gefüllte Badge (Zahl in Kreis) statt Farbpunkt — eindeutig
                            // von den Status-Punkten (u. a. rotes „Storniert") unterscheidbar.
                            <span className="flex items-center gap-1 font-body-sm text-body-sm" title={holidayName}>
                              <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-primary px-1 font-bold text-on-primary">
                                {date.getDate()}
                              </span>
                              {holidayName && <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" />}
                            </span>
                          ) : (
                            <span
                              className={`flex items-center gap-1 font-body-sm text-body-sm ${
                                inMonth ? "text-on-surface dark:text-inverse-on-surface" : "text-secondary-fixed-dim dark:text-outline"
                              }`}
                              title={holidayName}
                            >
                              {date.getDate()}
                              {/* M6: Feiertag mit eigenem Symbol statt bg-tertiary-Punkt (= „Bestätigt"). */}
                              {holidayName && <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" />}
                            </span>
                          )}
                        </div>

                        <div className="space-y-0.5">
                          {(offeneTage.has(key) ? dayItems : dayItems.slice(0, 2)).map((item) => (
                            <DayItemChip
                              key={`${item.kind}-${item.row.id}`}
                              item={item}
                              customerById={customerById}
                              onOpenEvent={openPreview}
                              onOpenNote={openNote}
                            />
                          ))}
                          {dayItems.length > 2 && (
                            /* Antoine, 14.10.2026: „+N weitere" klappt die restlichen Chips direkt in
                               der Zelle auf (die Zeile waechst in der Hoehe) — kein Sprung mehr in die
                               Wochenansicht. „weniger" klappt wieder zu. */
                            <button
                              type="button"
                              aria-expanded={offeneTage.has(key)}
                              onClick={() =>
                                setOffeneTage((alt) => {
                                  const neu = new Set(alt);
                                  if (neu.has(key)) neu.delete(key);
                                  else neu.add(key);
                                  return neu;
                                })
                              }
                              className="px-1.5 text-[10px] font-medium text-secondary hover:text-primary hover:underline dark:text-secondary-fixed-dim"
                            >
                              {offeneTage.has(key) ? "weniger" : `+${dayItems.length - 2} weitere`}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* G2: Wochenansicht — ein Zeilen-Grid, volle Event-Liste je Tag (scrollbar). */
                <div className="grid grid-cols-7 gap-px bg-outline-variant/20 dark:bg-outline/20">
                  {weekCells.map(({ date }, idx) => {
                    const key = ymd(date);
                    const isToday = key === todayKey;
                    const dayItems = byDay.get(key) ?? [];
                    const hasEvent = dayItems.some((it) => it.kind === "event");
                    const holidayName = holidaysByDate.get(key);
                    // L7: leerer Tag -> Klick legt „Neue Anfrage" mit vorbelegtem Datum an.
                    const isEmpty = dayItems.length === 0;
                    const openNewInquiry = () => navigate(`/anfragen?neu=1&datum=${key}`);
                    return (
                      <div
                        key={`${key}-${idx}`}
                        onClick={isEmpty ? openNewInquiry : undefined}
                        onKeyDown={
                          isEmpty
                            ? (ev) => {
                                if (ev.key === "Enter" || ev.key === " ") {
                                  ev.preventDefault();
                                  openNewInquiry();
                                }
                              }
                            : undefined
                        }
                        role={isEmpty ? "button" : undefined}
                        tabIndex={isEmpty ? 0 : undefined}
                        title={isEmpty ? `Neue Anfrage für den ${date.getDate()}. anlegen` : undefined}
                        className={`flex min-h-[420px] flex-col p-2 transition-colors ${isEmpty ? "cursor-pointer" : ""} ${
                          isToday
                            ? "bg-primary-fixed/30 dark:bg-primary-fixed/10"
                            : "bg-surface-container-lowest dark:bg-inverse-surface"
                        }`}
                      >
                        <div className="mb-1.5 flex items-center justify-between gap-1">
                          {hasEvent ? (
                            <button
                              type="button"
                              title={`Küchenzettel für ${key} drucken (Sammel-Druck)`}
                              disabled={printDate === key}
                              onClick={() => void printDay(key)}
                              className="rounded p-0.5 text-secondary-fixed-dim transition-colors hover:bg-surface-container-high hover:text-primary disabled:opacity-50 dark:hover:bg-surface-variant/40"
                            >
                              <Icon name="print" className="text-[14px]" />
                            </button>
                          ) : (
                            <span />
                          )}
                          {isToday ? (
                            // L8: „Heute" als gefüllte Badge (siehe Monatsansicht).
                            <span className="flex items-center gap-1 font-body-sm text-body-sm" title={holidayName}>
                              <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-primary px-1 font-bold text-on-primary">
                                {date.getDate()}
                              </span>
                              {holidayName && <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" />}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface" title={holidayName}>
                              {date.getDate()}
                              {/* M6: Feiertag mit eigenem Symbol statt bg-tertiary-Punkt. */}
                              {holidayName && <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" />}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 space-y-1 overflow-y-auto">
                          {dayItems.map((item) => (
                            <DayItemChip
                              key={`${item.kind}-${item.row.id}`}
                              item={item}
                              customerById={customerById}
                              onOpenEvent={openPreview}
                              onOpenNote={openNote}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {!loading && view === "month" && !narrow && (
        <p className="mt-4 flex items-center gap-1.5 text-[11.5px] text-secondary-fixed-dim">
          <Icon name="info" className="text-[14px]" />
          {itemsThisMonth.length} Termin{itemsThisMonth.length === 1 ? "" : "e"} im {monthLabel.split(" ")[0]} · Klick auf einen Chip zeigt die Vorschau
        </p>
      )}

      {/* Vorschau-Popover (Quick-Win 2026-07): auf schmalen Viewports faellt es auf den
          bestehenden Drawer zurueck statt ein eigenes Bottom-Sheet zu bauen (Vorgabe). `key`
          erzwingt beim Chip-Wechsel ein Remount, damit der motion-safe-Eintritts-Uebergang
          erneut spielt statt nur umzupositionieren. */}
      {preview &&
        (narrow ? (
          <Drawer
            open
            onClose={closePreview}
            title={firmaOderName(customerById.get(preview.event.customerId)) !== "—" ? firmaOderName(customerById.get(preview.event.customerId)) : (anlassAnzeige(preview.event.occasion) ?? "Termin")}
          >
            <EventPreviewBody
              base={preview.event}
              detail={detailQ.data}
              detailLoading={detailQ.loading}
              customer={customerById.get(preview.event.customerId)}
              onOpen={() => {
                closePreview();
                navigate(mitHier(vorgangPfad(preview.event.id), "Kalender"));
              }}
              onOffer={() => {
                closePreview();
                navigate(angebotPfad(preview.event.id));
              }}
              onPrint={() => {
                if (preview.event.eventDate) void printDay(preview.event.eventDate.slice(0, 10));
              }}
              printBusy={printDate === preview.event.eventDate?.slice(0, 10)}
            />
          </Drawer>
        ) : (
          <EventPreviewPopover key={preview.event.id} anchor={preview.anchor} onClose={closePreview}>
            <EventPreviewBody
              base={preview.event}
              detail={detailQ.data}
              detailLoading={detailQ.loading}
              customer={customerById.get(preview.event.customerId)}
              onOpen={() => {
                closePreview();
                navigate(mitHier(vorgangPfad(preview.event.id), "Kalender"));
              }}
              onOffer={() => {
                closePreview();
                navigate(angebotPfad(preview.event.id));
              }}
              onPrint={() => {
                if (preview.event.eventDate) void printDay(preview.event.eventDate.slice(0, 10));
              }}
              printBusy={printDate === preview.event.eventDate?.slice(0, 10)}
            />
          </EventPreviewPopover>
        ))}
    </>
  );
}

/** Punktfarbe einer Kalenderzeile — Notizen in Info-Blau wie ihr Chip. */
function punktFarbe(item: DayItem): string {
  return LEGENDE_PUNKT_FARBE[item.kind === "event" ? item.tone : "info"];
}

/** Sortierschlüssel innerhalb eines Tages: Uhrzeit, ganztägig/ohne Zeit zuerst. */
function zeitVon(item: DayItem): string | null {
  const t = item.kind === "event" ? item.row.timeFrom : item.row.dueTime;
  return t ? t.slice(0, 5) : null;
}

/** Bis zu drei Punkte (Status der Termine) plus „+“, wenn mehr — für Wochenstreifen und Punkte-Monat. */
function TagPunkte({ items }: { items: DayItem[] }) {
  return (
    <span className="flex h-[6px] items-center justify-center gap-[3px]">
      {items.slice(0, 3).map((it) => (
        <span key={`${it.kind}-${it.row.id}`} className={`h-[5px] w-[5px] rounded-full ${punktFarbe(it)}`} />
      ))}
      {items.length > 3 && <span className="text-[9px] font-bold leading-none text-secondary-fixed-dim">+</span>}
    </span>
  );
}

const TAGESLISTE_OFFEN_BIS = 3;

/**
 * Handy-Standard (Handy-Mockup 26.09.2026, Entscheidung 7): oben ein Wochenstreifen, in den alle
 * sieben Tage passen (Punkte zeigen den Status), darunter die Tage als Liste mit Uhrzeit, vollem
 * Namen, Gästen und Ort. Ab vier Terminen klappt der Rest zu. Ein leerer Tag bietet „Neue Anfrage
 * für diesen Tag“ an (wie die leere Zelle im Raster), der Küchenzettel-Druck bleibt je Tag.
 */
function KalenderTagesliste({
  tage,
  byDay,
  todayKey,
  holidaysByDate,
  fokusTag,
  onTag,
  customerById,
  printDate,
  onPrint,
  onNeu,
  onOpenEvent,
  onOpenNote,
}: {
  tage: Date[];
  byDay: Map<string, DayItem[]>;
  todayKey: string;
  holidaysByDate: Map<string, string>;
  fokusTag: string | null;
  onTag: (date: Date) => void;
  customerById: Map<string, Customer>;
  printDate: string | null;
  onPrint: (key: string) => void;
  onNeu: (key: string) => void;
  onOpenEvent: (e: EventRow) => void;
  onOpenNote: (t: Task & { eventId: string }) => void;
}) {
  const [aufgeklappt, setAufgeklappt] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (fokusTag) document.getElementById(`kalender-tag-${fokusTag}`)?.scrollIntoView?.({ block: "start" });
  }, [fokusTag]);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-7 gap-1 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-1.5 dark:border-outline/30 dark:bg-inverse-surface">
        {tage.map((d) => {
          const key = ymd(d);
          const heute = key === todayKey;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onTag(d)}
              aria-label={d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}
              className={`flex flex-col items-center gap-1 rounded-lg py-1.5 ${heute ? "bg-primary text-on-primary" : "text-on-surface dark:text-inverse-on-surface"}`}
            >
              <span className={`text-[10.5px] font-semibold uppercase ${heute ? "" : "text-secondary dark:text-secondary-fixed-dim"}`}>{WEEKDAYS[(d.getDay() + 6) % 7]}</span>
              <span className="text-[15px] font-bold leading-none">{d.getDate()}</span>
              <TagPunkte items={byDay.get(key) ?? []} />
            </button>
          );
        })}
      </div>
      {tage.map((d) => {
        const key = ymd(d);
        const items = [...(byDay.get(key) ?? [])].sort((a, b) => (zeitVon(a) ?? "").localeCompare(zeitVon(b) ?? ""));
        const heute = key === todayKey;
        const feiertag = holidaysByDate.get(key);
        const offen = aufgeklappt.has(key) || items.length <= TAGESLISTE_OFFEN_BIS;
        const sichtbar = offen ? items : items.slice(0, TAGESLISTE_OFFEN_BIS);
        return (
          <section
            key={key}
            id={`kalender-tag-${key}`}
            data-testid={`kalender-tag-${key}`}
            className="scroll-mt-24 overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-lowest dark:border-outline/30 dark:bg-inverse-surface"
          >
            <header className="flex items-center gap-2 border-b border-outline-variant/30 px-3.5 py-2.5 dark:border-outline/30">
              <h2 className={`text-[13px] font-bold ${heute ? "text-primary dark:text-primary-fixed" : "text-on-background dark:text-inverse-on-surface"}`}>
                {d.toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "long" })}
                {heute && " · heute"}
              </h2>
              {feiertag && (
                <span className="flex items-center gap-1 text-[11px] text-secondary-fixed-dim">
                  <Icon name="flag" className="text-[13px]" /> {feiertag}
                </span>
              )}
              {items.length > 0 && (
                <span className="text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                  {items.length} Termin{items.length === 1 ? "" : "e"}
                </span>
              )}
              {items.some((it) => it.kind === "event") && (
                <button
                  type="button"
                  onClick={() => onPrint(key)}
                  disabled={printDate === key}
                  className="ml-auto flex items-center gap-1 rounded-md px-1.5 py-1 text-[11.5px] font-semibold text-secondary hover:text-primary disabled:opacity-50 dark:text-secondary-fixed-dim"
                >
                  <Icon name="print" className="text-[15px]" /> Küchenzettel
                </button>
              )}
            </header>
            {items.length === 0 ? (
              <button
                type="button"
                onClick={() => onNeu(key)}
                className="flex w-full items-center gap-2 px-3.5 py-3 text-left text-[12.5px] text-secondary hover:text-primary dark:text-secondary-fixed-dim"
              >
                <Icon name="add_circle" className="text-[17px]" /> Noch frei · Neue Anfrage für diesen Tag
              </button>
            ) : (
              <ul className="divide-y divide-outline-variant/20 dark:divide-outline/20">
                {sichtbar.map((it) => {
                  const text = it.kind === "event" ? eventChipText(it.row, customerById.get(it.row.customerId)) : noteChipText(it.row);
                  return (
                    <li key={`${it.kind}-${it.row.id}`}>
                      <button
                        type="button"
                        data-testid={`kalender-zeile-${it.kind}-${it.row.id}`}
                        onClick={() => (it.kind === "event" ? onOpenEvent(it.row) : onOpenNote(it.row))}
                        className={`flex w-full items-start gap-3 px-3.5 py-2.5 text-left hover:bg-surface-container-low/40 ${it.kind === "event" && it.row.status === "completed" ? "opacity-60" : ""}`}
                      >
                        <span className="w-11 shrink-0 pt-px text-[12px] font-semibold tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim">
                          {zeitVon(it) ?? "ganzt."}
                        </span>
                        <span className={`mt-[6px] h-[7px] w-[7px] shrink-0 rounded-full ${punktFarbe(it)}`} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-semibold leading-snug text-on-background dark:text-inverse-on-surface">{text.title}</span>
                          {text.meta && <span className="block text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{text.meta}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
                {!offen && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setAufgeklappt((alt) => new Set(alt).add(key))}
                      className="flex w-full items-center justify-center gap-1 px-3.5 py-2.5 text-[12px] font-semibold text-primary dark:text-primary-fixed"
                    >
                      {items.length - TAGESLISTE_OFFEN_BIS} weitere anzeigen <Icon name="expand_more" className="text-[16px]" />
                    </button>
                  </li>
                )}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

/**
 * Handy-Monat (Entscheidung 7): sieben Spalten mit Text sind auf 390 px nicht lesbar — darum nur
 * Tageszahl und Status-Punkte. Tippen öffnet die Tagesliste dieser Woche beim Tag.
 */
function KalenderMonatPunkte({
  cells,
  byDay,
  todayKey,
  holidaysByDate,
  onTag,
}: {
  cells: Cell[];
  byDay: Map<string, DayItem[]>;
  todayKey: string;
  holidaysByDate: Map<string, string>;
  onTag: (date: Date) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-lowest dark:border-outline/30 dark:bg-inverse-surface">
      <div className="grid grid-cols-7 border-b border-outline-variant/50 dark:border-outline/30">
        {WEEKDAYS.map((w) => (
          <div key={w} className="py-2 text-center text-[10.5px] font-semibold uppercase text-secondary dark:text-secondary-fixed-dim">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map(({ date, inMonth }) => {
          const key = ymd(date);
          const items = byDay.get(key) ?? [];
          const heute = key === todayKey;
          return (
            <button
              key={key}
              type="button"
              data-testid={`kalender-monat-tag-${key}`}
              onClick={() => onTag(date)}
              title={holidaysByDate.get(key)}
              aria-label={`${date.toLocaleDateString("de-DE", { day: "numeric", month: "long" })}: ${items.length} Termin${items.length === 1 ? "" : "e"}`}
              className="flex h-12 flex-col items-center justify-center gap-1"
            >
              <span
                className={`grid h-6 min-w-[24px] place-items-center rounded-full px-1 text-[12.5px] ${
                  heute ? "bg-primary font-bold text-on-primary" : inMonth ? "text-on-surface dark:text-inverse-on-surface" : "text-secondary-fixed-dim dark:text-outline"
                }`}
              >
                {date.getDate()}
              </span>
              <TagPunkte items={items} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
